/**
 * Zod schemas — the single source of truth for data shapes.
 *
 * With TypeScript removed these replace the former interfaces. They are used
 * to validate request bodies on the server AND to drive forms on the client,
 * so a shape is written down exactly once.
 */
import { z } from 'zod';
import { ORDER_TYPES, PAYMENT_METHODS, INVENTORY_TRANSACTION_TYPES } from '../constants/index.js';

export const uuid = z.string().uuid();

/** Authenticated user attached to the request context. (was: interface AuthUser) */
export const authUserSchema = z.object({
  id: uuid,
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  branchId: uuid.optional(),
});

/**
 * Who is signing in: an email address, or a phone number.
 *
 * WHY THE FIELD IS STILL CALLED `email`
 * -------------------------------------
 * Because renaming it would refuse every phone already in a rider's pocket. The
 * apps in the field send `email`, and an APK is not updated the way a web page
 * is — so the field keeps its name and widens what it accepts.
 *
 * WHAT IT USED TO REJECT
 * ----------------------
 * `z.string().email()`. The phone's own sign-in field says "Phone number or
 * email" and shows a phone number as its example, so the app had to dress a
 * phone number up as an email to get past this line — which is how a cashier's
 * number became a rider address that has never existed.
 */
const emailOrPhone = z
  .string()
  .trim()
  .min(1, 'Who is signing in?')
  .refine(
    (typed) =>
      typed.includes('@')
        ? z.string().email().safeParse(typed).success
        : // Ten significant digits: a Pakistani mobile number is 3XXXXXXXXX,
          // and what precedes it is a country or trunk prefix.
          typed.replace(/\D/g, '').length >= 10,
    'Enter an email address or a phone number'
  );

export const loginSchema = z.object({
  email: emailOrPhone,
  password: z.string().min(1),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
});

/** (was: interface OrderItemInput) */
export const orderItemInputSchema = z.object({
  productId: uuid,
  variantId: uuid.optional(),
  quantity: z.number().int().positive(),
  modifiers: z.array(z.object({ optionId: uuid })).optional(),
  notes: z.string().optional(),
  /**
   * For a deal: the items the customer chose to fill its slots, as picked at
   * the till. Quantities are per one of the deal — the kitchen and the bill
   * multiply by how many of the deal were ordered.
   */
  components: z
    .array(
      z.object({
        productId: uuid,
        quantity: z.number().int().positive(),
        // The chosen item's own size/extras, when it has them.
        modifiers: z.array(z.object({ optionId: uuid })).optional(),
      })
    )
    .optional(),
});

/** (was: interface CreateOrderInput) — the most important boundary in the system. */
export const createOrderSchema = z.object({
  orderType: z.enum(ORDER_TYPES),
  branchId: uuid.optional(),
  terminalId: uuid.optional(),
  customerId: uuid.optional(),
  customerName: z.string().optional(),
  customerPhone: z.string().optional(),
  /** The Foodpanda app's own order number, keyed in by the cashier. */
  externalOrderId: z.string().max(64).optional(),
  deliveryAddress: z.string().optional(),
  /** Which zone a delivery is going to — sets the fee and the minimum. */
  deliveryZoneId: uuid.optional(),
  /**
   * A delivery fee set by hand for this one order, overriding the zone's fee
   * or the flat default — a longer trip, a regular waved through, a round
   * figure agreed on the phone. Left off, the fee is worked out as before.
   */
  deliveryFee: z.number().min(0).optional(),
  /**
   * Where the door actually is.
   *
   * Bounded to the real range of a coordinate, because a number outside it is
   * not a place and there is no sensible thing to do with it downstream — a
   * rider sent to latitude 400 gets a maps app that opens on nothing.
   *
   * Optional, and expected to be absent for a long while: an address that has
   * never been pinned has no point, and refusing the order for want of one
   * would stop the shop trading over a convenience.
   */
  deliveryLatitude: z.number().min(-90).max(90).optional(),
  deliveryLongitude: z.number().min(-180).max(180).optional(),
  tableNumber: z.string().optional(),
  items: z.array(orderItemInputSchema).min(1, 'Order must have at least one item'),
  /**
   * Bounded here as a first line, and again in the pricing service.
   *
   * Unbounded, a cashier's own token could send 500% off and produce a total
   * of −76,792 — which posts negative revenue into the ledger, where every
   * report inherits it. A negative value overcharged the customer instead.
   *
   * The service is what actually enforces this, because a rule that lives
   * only in a schema is a rule any other caller can skip.
   */
  discounts: z
    .array(
      z.object({
        type: z.enum(['percentage', 'fixed', 'manager', 'loyalty']),
        value: z.number().nonnegative(),
        reason: z.string().optional(),
      })
    )
    .optional(),
  /** An offer, applied by the code a cashier types or picks. */
  promotionCode: z.string().trim().min(1).max(40).optional(),
  /**
   * A manager authorising a discount over the cashier's limit.
   *
   * Sent only when one is needed. The till learns it is needed by being
   * refused once, which is deliberate: asking for a manager's PIN before
   * knowing whether the discount is even over the limit would train everybody
   * to fetch a manager for a five per cent goodwill gesture.
   *
   * Verified at the branch, never trusted from here.
   */
  approval: z
    .object({
      by: z.string().trim().min(1).max(200),
      pin: z.string().min(4).max(6),
    })
    .optional(),
  /** Which table a dine-in order is sitting at. */
  tableId: z.string().optional(),
  /**
   * The name the caller gave this order before sending it.
   *
   * Only a caller that may have to send the same order twice needs one — in
   * practice a phone, which cannot tell a lost reply from a lost request. Send
   * the same reference again and the same order comes back rather than a
   * second one being written.
   *
   * A uuid because it has to be unique across every phone in every branch with
   * nobody coordinating, and because a guessable reference would let one
   * caller collide with another's order.
   */
  clientRef: uuid.optional(),
  /**
   * When the customer wants it, if not now.
   *
   * Null on almost every order. Making an immediate sale carry a timestamp
   * equal to its own creation would put a scheduling concept into every order
   * that never needed one.
   */
  scheduledFor: z.string().datetime({ offset: true }).optional(),
  notes: z.string().optional(),
  specialInstructions: z.string().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  /**
   * The customer settled a delivery before it left — paid online, in advance.
   *
   * Needed because on a delivery the payment method says how the money will
   * arrive, not that it has. Without this flag every delivery would either be
   * treated as already paid, which sends riders out to collect nothing, or as
   * never paid, which loses track of the ones genuinely settled online.
   */
  prepaid: z.boolean().optional(),
  /**
   * Put this bill on a staff member's account instead of taking money for it.
   *
   * The id of the staff member who owes it. When set, the bill is not paid at
   * the counter — it books as a sale owed by that person (a receivable), the
   * way a rider's COD does, and a manager clears the tab later. Any payment
   * method sent alongside is ignored.
   */
  staffAccountId: z.string().optional(),
  paidAmount: z.number().nonnegative().optional(),
  payments: z
    .array(
      z.object({
        method: z.enum(PAYMENT_METHODS),
        amount: z.number().nonnegative(),
      })
    )
    .optional(),
});

/**
 * Placing a dine-in order means saying which table.
 *
 * WHY THIS IS REFUSED RATHER THAN ALLOWED THROUGH
 * -----------------------------------------------
 * Without it the order exists and belongs nowhere. The kitchen makes the food
 * and the ticket says nothing about where it goes; the floor plan shows the
 * table free, so a waiter can seat somebody else at it; and the "unpaid on the
 * floor" figure is short by that bill. No screen can recover the answer,
 * because it was never asked for.
 *
 * An order with nobody sitting at a table is a takeaway, and the till has a
 * button for that.
 *
 * WHY THIS IS NOT ON `createOrderSchema` ITSELF
 * ---------------------------------------------
 * The quote endpoint validates with the same schema, and a quote is a pricing
 * question asked while the cashier is still adding items — often before anyone
 * has chosen a table. Putting the rule on the shared schema meant the cart
 * could not show a total at all until a table was picked, which is a worse
 * problem than the one being fixed. Asking what something costs stays free;
 * committing to it is where the table is required.
 */
export const placeOrderSchema = createOrderSchema.refine(
  (order) => order.orderType !== 'dine_in' || Boolean(order.tableId || order.tableNumber),
  {
    message: 'Pick a table for a dine-in order, or change it to takeaway.',
    path: ['tableId'],
  }
);

/** (was: interface JournalLine) */
export const journalLineSchema = z.object({
  accountCode: z.string(),
  debit: z.number().nonnegative(),
  credit: z.number().nonnegative(),
  description: z.string().optional(),
  branchId: uuid.optional(),
});

/** (was: interface CreateJournalEntry) */
export const createJournalEntrySchema = z.object({
  branchId: uuid.optional(),
  description: z.string(),
  sourceType: z.enum([
    'sale',
    'purchase',
    'payment',
    'receipt',
    'expense',
    'adjustment',
    'transfer',
    'opening',
    'closing',
    'manual',
  ]),
  sourceId: z.string().optional(),
  lines: z.array(journalLineSchema).min(2, 'Journal entry must have at least 2 lines'),
  createdBy: uuid,
  entryDate: z.string().optional(),
});

/** (was: interface InventoryMovement) */
export const inventoryMovementSchema = z.object({
  ingredientId: uuid,
  warehouseId: uuid,
  branchId: uuid,
  transactionType: z.enum(INVENTORY_TRANSACTION_TYPES),
  quantity: z.number(),
  unitId: uuid,
  unitCost: z.number().nonnegative(),
  referenceType: z.string().optional(),
  referenceId: z.string().optional(),
  notes: z.string().optional(),
  createdBy: uuid,
});

/** (was: interface AuditEntry) */
export const auditEntrySchema = z.object({
  userId: uuid.optional(),
  branchId: uuid.optional(),
  module: z.string(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().optional(),
  previousValue: z.unknown().optional(),
  newValue: z.unknown().optional(),
  ipAddress: z.string().optional(),
  deviceInfo: z.string().optional(),
  sessionId: z.string().optional(),
  reason: z.string().optional(),
  severity: z.enum(['info', 'warning', 'critical']).optional(),
});

export const openShiftSchema = z.object({
  openingAmount: z.number().nonnegative().default(0),
  terminalId: uuid.optional(),
  /** Which shift this is — 'morning' or 'evening'; prints on the close report. */
  shiftName: z.string().max(20).optional(),
  notes: z.string().optional(),
});

export const closeShiftSchema = z.object({
  actualCash: z.number().nonnegative(),
  denominations: z.record(z.string(), z.number()).optional(),
  notes: z.string().optional(),
  /**
   * "Yes, I know those tables have not paid, close it anyway."
   *
   * Absent by default, so a till that has not been taught about unpaid bills
   * is stopped and told rather than closing quietly. Sent only after the
   * cashier has been shown the list — see findUnsettledBills.
   */
  closeWithUnpaidBills: z.boolean().optional(),
});
